(ns dre.network
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.app.api :as api]
   [dre.components.flash.events :as-alias flash]
   [dre.config :as conf]
   [dre.i18n.utils :refer [decide-language]]
   [dre.i18n.views :refer [tr]]
   [dre.processes.bootstrap :as bs]
   [dre.storage.events :as-alias storage]
   [lambdaisland.uri]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as fetch-fx]))

(defn render-uri
  "Replace uri parameters with context values.
   (render-uri \"/users/:id/\" {:id 1}) => \"/users/1\"
  "
  [uri context]
  (let [uri (lambdaisland.uri/uri uri)
        context (or context {})]
    (update uri :path #(str/replace % #":([^\s\?/]+)" (comp context keyword second)))))

(defn- ->api-uri
  [db uri]
  (lambdaisland.uri/join (conf/->api-url db) uri))


;; -- Events

(rf/reg-event-fx
  ::request
  [(rf/inject-cofx ::storage/get :language) rf/trim-v]
  (fn-traced
    [{language :language db :db} [resource fetch-params]]
    {:pre [(contains? api/endpoints (:endpoint resource))]}
    (let [{endpoint-id :endpoint, ctx :context, request-timeout-s :request-timeout-s} resource
          {:keys [method uri static? timeout-s]} (get api/endpoints endpoint-id)
          uri (render-uri uri ctx)
          uri (if static?
                uri
                (->api-uri db uri))
          lang-headers {"Accept-Language"
                        (decide-language
                         :preferred language
                         :supported (apply sorted-set (conf/->supported-languages db))
                         :default (conf/->default-language db))}
          add-failure-auth-wrapper-fn (fn [params]
                                        (merge params
                                               {:on-failure [::failure-auth-wrapper {:on-failure-fx (get params :on-failure)}]}))]
      {::fetch-fx/fetch
       (-> (merge
            {:method method
             :request-id endpoint-id
             :url uri
             :timeout (cond request-timeout-s
                            (-> request-timeout-s
                                (* 1000))
                            timeout-s
                            (-> timeout-s
                                (* 1000))
                            :else
                            (-> db
                                conf/->default-request-timeout-s
                                (* 1000)))
             ;; https://developer.mozilla.org/en-US/docs/Web/API/Request/credentials
             :credentials (if static?
                            :omit
                            :include)
             ;; https://developer.mozilla.org/en-US/docs/Web/API/Request/mode
             :mode :cors
             :request-content-type :json
             :response-content-type :json
             :response-content-types {#"application/.*json" :json
                                      "text/plain" :text}
             :on-failure [::default-failure {:endpoint endpoint-id :url uri}]}
            (-> fetch-params
                (update :headers merge (conf/->standard-headers db) lang-headers)))
                (add-failure-auth-wrapper-fn))})))

(defn custom-abort
  [{:keys [request-id]}]
  (let [js-abort-controller (get @fetch-fx/request-id->js-abort-controller request-id)]
    (when js-abort-controller
      (swap! fetch-fx/request-id->js-abort-controller dissoc request-id)
      (.abort js-abort-controller))))

(rf/reg-event-fx
  ::request-with-abort
  [rf/trim-v]
  (fn-traced
   [{_db :db} [resource fetch-params]]
   {:pre [(contains? api/endpoints (:endpoint resource))]}
   (let [{endpoint-id :endpoint} resource
         _ (custom-abort {:request-id endpoint-id})]
     {:dispatch-later {:ms 100
                       :dispatch [::request resource fetch-params]}})))

(rf/reg-event-fx
 ::abort
 [rf/trim-v]
 (fn-traced
  [{_db :db} [request-id]]
  {::fetch-fx/abort {:request-id request-id}}))

(rf/reg-event-fx
 ::failure-auth-wrapper
 [rf/trim-v]
 (fn-traced
  [_ [{:keys [::fetch-fx/response on-failure-fx]} no-envelope-response]]
  (let [resp (or response no-envelope-response)
        status (get resp :status)]
    (if (and (contains? #{401 403} status) (not (= (first on-failure-fx) ::bs/auth-nok)))
      {:fx [[:dispatch [::flash/show
                        [:span [:b [tr "Your session has timed out. Please log in again to resume your work."]]]
                        :severity :error]]
            [:dispatch-later {:ms 1000
                              :dispatch [::bs/redirect-to-sso]}]]}
      {:dispatch (assoc-in on-failure-fx [1 ::fetch-fx/response] resp)}))))

(rf/reg-event-fx
  ::default-failure
  [rf/trim-v]
  (fn-traced
   [_ [{::fetch-fx/keys [response]}]]
   {:fx [[:dispatch [::flash/show
                     [:span [:b [tr "Sorry, there was a problem. We filed the error. "] [:br]]
                      (or (get-in response [:body :error :message])
                          (:status-text response)
                          (:problem-message response)
                          "Network error")]
                     :severity :error]]]}))


;; -- Effects

(rf/reg-fx
  ::request
  (fn [[resource fetch-params]]
    (rf/dispatch [::request resource fetch-params])))


(rf/reg-fx
  ::abort
  (fn [request-id]
     (rf/dispatch [::abort request-id])))

(rf/reg-fx
 ::request-with-abort
 (fn [[resource fetch-params]] 
   (rf/dispatch [::request-with-abort resource fetch-params])))